import axios, { AxiosRequestConfig, AxiosError, Method } from "axios";
import { JobSearchRequest } from "@themuse/design-system";
import getConfig from "next/config";
import NodeCache from "node-cache";

import { axiosClient } from "utils/axiosClient";
import {
    ArticleListRequest,
    ArticleListResult,
    CompanyCulturesRequest,
    Culture,
    EmployeeStoriesRequest,
    EmployeeStory,
    ORMSingleCompany,
    SingleCompany,
    SingleCompanyRequest,
} from "interfaces/api/apiInterfaces";
import mockSingleCompany from "mocks/mockSingleCompany.json";
import { isBrowser } from "./browser";
import {
    jobQueryCompanyShortnameMaxlength,
    jobSearchQueryMaxlength
} from "./constants";
import { formatLog } from "./logging";
import { compressedCompany } from "./orm";

export interface CompanyValue {
    object: "company_name_suggestion",
    // eslint-disable-next-line camelcase
    short_name: string
}

export interface CompanySuggestion {
    object: "suggestion",
    type: "company_name",
    text: string
    value: CompanyValue,
    image: string
}

// Expire once a day, check every 5 minutes
const apiCache = new NodeCache({ stdTTL: 86400, checkperiod: 300 });

export const getJobRecommendations = async () => {
    const { publicRuntimeConfig: { apiBase, apiRequestTimeoutMs } } = getConfig();
    const response = await axiosClient.get(`${apiBase}/recommendations`, { timeout: apiRequestTimeoutMs })
        .catch((error: AxiosError) => {
            const msg = `Error requesting recommendations at ${apiBase}/recommendations.`;
            const err = JSON.stringify(formatLog({ error }));
            console.error(msg, err);
        })
        .then((res: any) => res?.data);
    return response;
};

/**
 * Formatter for The Muse Jobs.
 * This is the default.
 */

const getFormattedQueryParams = (queryParams: JobSearchRequest = null): any => ({
    ...queryParams,
    ...(queryParams.query && { query: queryParams.query.substring(0, jobSearchQueryMaxlength) })
});

/**
 * Returns the Axios config for requesting Jobs from TheMuse.
 */
const getMuseJobsConfig = (
    queryParams: JobSearchRequest,
    cookies?: string
): AxiosRequestConfig => {
    const { serverRuntimeConfig: { jobsApiHost } } = getConfig();
    const { publicRuntimeConfig: { apiBase, apiRequestTimeoutMs } } = getConfig();

    const path = isBrowser()
        ? `${apiBase}/jobs?limit=5`
        : `${jobsApiHost}/api/v2/search/jobs?limit=5`;

    const formattedQueryParams = getFormattedQueryParams(queryParams);

    const museConfig: AxiosRequestConfig = {
        method: ("GET" as Method),
        url: path,
        headers: isBrowser() ? {} : {
            Cookie: cookies || ""
        },
        params: {
            ...formattedQueryParams,
            timeout: apiRequestTimeoutMs
        }
    };

    return museConfig;
};

/**
 * Request jobs directly from the backend API or proxied through the Next.js internal API.
 */
export const fetchJobs = async (queryParams: JobSearchRequest, cookies?: string) => {
    const axiosConfig: AxiosRequestConfig = getMuseJobsConfig(queryParams, cookies);

    try {
        const response = await axios(axiosConfig);
        return response.data;
    } catch (error) {
        const msg = `Company Renderer was unable to request job results from ${axiosConfig.url}`;
        const err = JSON.stringify(formatLog({ error }));
        console.error(msg, err);
        throw new Error(msg);
    }
};

/**
 * Request articles directly from the backend API or proxied through the Next.js internal API.
 */
// eslint-disable-next-line max-len
export const fetchArticles = async (queryParams: Partial<ArticleListRequest>, cookies?: string): Promise<ArticleListResult> => {
    const browser = isBrowser();
    const {
        publicRuntimeConfig: { apiBase },
        serverRuntimeConfig: { articleApiHost },
    } = getConfig();
    const apiPathname = `companies/${queryParams.company_short_name}/filtered-articles`;
    const url = browser
        ? `${apiBase}/${apiPathname}`
        : `${articleApiHost}/api/v1/${apiPathname}`;

    const defaultParams: ArticleListRequest = {
        company_short_name: "",
        orderBy: "dateUpdated+desc",
        page: "0",
        type: "articles,listicles",
    };

    const requestParams = new URLSearchParams({ ...defaultParams, ...queryParams })
        .toString()
        .replace(/%2B|%2C/g, decodeURIComponent);

    const axiosConfig: AxiosRequestConfig = {
        method: ("GET" as Method),
        url: `${url}?${requestParams}`,
        headers: browser ? {} : {
            "Content-Type": "application/json",
            Cookie: cookies || ""
        },
        timeout: 30_000,
    };

    try {
        const response = await axios(axiosConfig).then((r) => r?.data || r);
        return response;
    } catch (error) {
        const msg = `Company Renderer was unable to request article results from ${axiosConfig.url}`;
        const err = JSON.stringify(formatLog({ error }));
        console.error(msg, err);
        throw new Error(msg);
    }
};

export const getSingleCompany = async (queryParams: SingleCompanyRequest): Promise<ORMSingleCompany> => {
    const {
        publicRuntimeConfig: { apiBase, apiRequestTimeoutMs },
        serverRuntimeConfig: { companyApiHost, companyApiToken, mockApiResponses }
    } = getConfig();
    const { companyShortName } = queryParams;
    const config: AxiosRequestConfig = {
        timeout: apiRequestTimeoutMs,
        headers: {
            Authorization: `Bearer ${companyApiToken}`
        }
    };

    // If either query param length exceeds the maximum, throw a malformed request error
    if (companyShortName?.length > jobQueryCompanyShortnameMaxlength) {
        // eslint-disable-next-line max-len
        const msg = `Malformed Request: The companyShortName parameter exceeds maximum length of ${jobQueryCompanyShortnameMaxlength}.`;
        throw new Error(msg);
    }

    const path = isBrowser()
        ? `${apiBase}/company/${companyShortName}`
        : `${companyApiHost}/api/v1/companies/${companyShortName}`;

    // Fetch the company from the API.
    const response = mockApiResponses
        ? { data: mockSingleCompany }
        : await axiosClient.get(path, config)
            .catch((error: AxiosError) => {
                const msg = `Error requesting company at ${path}.`;
                const err = JSON.stringify(formatLog({ error }));
                console.error(msg, err);
            });

    const apiCompanyRequest: SingleCompany = (response as any)?.data;
    const company = compressedCompany(apiCompanyRequest);
    return company as ORMSingleCompany;
};

/**
 * Request the user.
 */
export const fetchUser = async (axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, apiBase } } = getConfig();
    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
            }
        },
        ...axiosOptions
    };
    const path = `${apiBase}/user`;
    try {
        const response = await axios(path, config);
        return response.data;
    } catch (error) {
        const msg = `Company Renderer was unable to request the user from ${path}.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * Request user from the service API.
 */
export const fetchApiUser = async (oskarId = "", sessionId = "", axiosOptions?: AxiosRequestConfig) => {
    const {
        publicRuntimeConfig: {
            apiRequestTimeoutMs, museApiHost, museHost
        }
    } = getConfig();
    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
                cookie: `oskar_id=${oskarId}; session_id=${sessionId}`,
                referer: museHost
            }
        },
        ...axiosOptions
    };
    const path = `${museApiHost}/api/user`;
    try {
        const response = await axios(path, config);
        return response;
    } catch (error) {
        const msg = `Company Renderer was unable to request the user from ${path}.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * Request company suggestions.
 */
export const fetchCompanySuggestions = async (inputValue: string, axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, apiBase } } = getConfig();
    const path = `${apiBase}/company-suggestion/${inputValue}`;
    try {
        const response = await axios(path, { ...axiosOptions, ...{ timeout: apiRequestTimeoutMs } });
        return response.data;
    } catch (error) {
        const msg = `Error requesting company suggestions from ${path}`;
        const err = JSON.stringify(formatLog({ error }));
        console.error(msg, err);
        return null;
    }
};

export interface CompanySuggestorResults {
    object: "suggestions",
    data: CompanySuggestion[]
}

/**
 * Request company suggestions from the service API.
 */
export const fetchApiCompanySuggestions = async (query: string): Promise<CompanySuggestorResults> => {
    const { serverRuntimeConfig: { apiRequestTimeoutMs, jobsApiHost } } = getConfig();
    // if query exists we will pass it in the params.
    const queryObj = query ? { query } : {};
    const config: AxiosRequestConfig = { params: { type: "company_name", ...queryObj }, timeout: apiRequestTimeoutMs };
    const path = `${jobsApiHost}/api/v2/suggestions`;
    try {
        const response = await axios(path, config);
        return response.data;
    } catch (error) {
        const msg = `Error requesting jobs from ${path}`;
        const err = JSON.stringify(formatLog({ error }));
        console.error(msg, err);
        return null;
    }
};

export const getCulturesList = async (queryParams: CompanyCulturesRequest): Promise<Culture[]> => {
    const {
        publicRuntimeConfig: { apiBase, apiRequestTimeoutMs },
        serverRuntimeConfig: { companyApiHost, companyApiToken, mockApiResponses }
    } = getConfig();
    const { companyShortName } = queryParams;
    const cacheKey = `cultures_${companyShortName}`;
    const config: AxiosRequestConfig = {
        timeout: apiRequestTimeoutMs,
        headers: {
            Authorization: `Bearer ${companyApiToken}`
        }
    };

    // If either query param length exceeds the maximum, throw a malformed request error
    if (companyShortName?.length > jobQueryCompanyShortnameMaxlength) {
        // eslint-disable-next-line max-len
        const msg = `Malformed Request: The companyShortName parameter exceeds maximum length of ${jobQueryCompanyShortnameMaxlength}.`;
        throw new Error(msg);
    }

    const path = isBrowser()
        ? `${apiBase}/company/${companyShortName}/culture/list`
        : `${companyApiHost}/api/v1/companies/${companyShortName}/culture/list`;

    // Check for the cultures in cache.
    const cachedCultures = apiCache.get(cacheKey);

    if (cachedCultures === undefined) {
        // Fetch the cultures from the API.
        const response = mockApiResponses
            ? { data: mockSingleCompany }
            : await axiosClient.get(path, config)
                .catch((error: AxiosError) => {
                    const msg = `Error requesting company at ${path}.`;
                    const err = JSON.stringify(formatLog({ error }));
                    console.error(msg, err);
                });

        // eslint-disable-next-line camelcase
        const cultures: Culture[] = (response as any)?.data?.data?.filter(({ is_active }) => !!is_active);
        apiCache.set(cacheKey, cultures);
        console.info(`Cache refreshed, ${companyShortName} cultures retrieved from API.`);
        return cultures;
    }

    console.info(`Cache existing, ${companyShortName} cultures retrieved from NodeCache.`);
    return cachedCultures as Culture[];
};

export const getEmployeeStories = async (queryParams: EmployeeStoriesRequest): Promise<EmployeeStory[]> => {
    const {
        publicRuntimeConfig: { apiBase, apiRequestTimeoutMs },
        serverRuntimeConfig: { companyApiHost, companyApiToken, mockApiResponses }
    } = getConfig();
    const { companyShortName, culture } = queryParams;
    const cacheKey = `employeeStories_${companyShortName}_${culture}`;
    const config: AxiosRequestConfig = {
        timeout: apiRequestTimeoutMs,
        headers: {
            Authorization: `Bearer ${companyApiToken}`
        }
    };

    // If either query param length exceeds the maximum, throw a malformed request error
    if ([companyShortName, culture].some((s) => s?.length > jobQueryCompanyShortnameMaxlength)) {
        // eslint-disable-next-line max-len
        const msg = `Malformed Request: A request parameter exceeds maximum length of ${jobQueryCompanyShortnameMaxlength}.`;
        throw new Error(msg);
    }

    const cultureDirectory = culture === "all" ? "" : `/culture/${culture}`;
    const path = isBrowser()
        ? `${apiBase}/company/${companyShortName}/${culture}/employee_story/list`
        : `${companyApiHost}/api/v1/companies/${companyShortName}${cultureDirectory}/employee_story/list`;

    // Check for the cultures in cache.
    const cachedResponse = apiCache.get(cacheKey);

    if (cachedResponse === undefined) {
        // Fetch the cultures from the API.
        const response = mockApiResponses
            ? { data: mockSingleCompany }
            : await axiosClient.get(path, config)
                .catch((error: AxiosError) => {
                    const msg = `Error requesting employee stories at ${path}.`;
                    const err = JSON.stringify(formatLog({ error }));
                    console.error(msg, err);
                });

        // eslint-disable-next-line camelcase
        const employeeStories: EmployeeStory[] = (response as any)?.data?.data?.filter(({ is_active }) => !!is_active);
        apiCache.set(cacheKey, employeeStories);
        // eslint-disable-next-line max-len
        console.info(`Cache refreshed, ${companyShortName} employee stories for ${culture} culture retrieved from API.`);
        return employeeStories;
    }

    // eslint-disable-next-line max-len
    console.info(`Cache existing, ${companyShortName} employee stories for ${culture} culture retrieved from NodeCache.`);
    return cachedResponse as EmployeeStory[];
};
